/**
    * @license
    * Copyright 2023 Google LLC. All Rights Reserved.
    * Licensed under the Apache License, Version 2.0 (the "License");
    * you may not use this file except in compliance with the License.
    * You may obtain a copy of the License at
    *
    * http://www.apache.org/licenses/LICENSE-2.0
    *
    * Unless required by applicable law or agreed to in writing, software
    * distributed under the License is distributed on an "AS IS" BASIS,
    * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    * See the License for the specific language governing permissions and
    * limitations under the License.
    * =============================================================================
    */
import * as t from "@tensorflow/tfjs-core";
import { Tensor as e, browser as n, tidy as r, cast as i, greater as o, scalar as a, oneHot as s, expandDims as u, range as f, matMul as l, reshape as c, add as d, sub as h, mul as p, argMax as m, squeeze as g, sigmoid as v, util as w, div as y, tensor as b, backend as S, getBackend as x, engine as M, equal as k, image as E, pad3d as T, tensor2d as P, slice as I, pad as O, mirrorPad as _ } from "@tensorflow/tfjs-core";
import { loadGraphModel as H } from "@tensorflow/tfjs-converter";
import { SelfieSegmentation as A } from "@mediapipe/selfie_segmentation";
var R = function (t, e) {
  return R = Object.setPrototypeOf || {
    __proto__: []
  } instanceof Array && function (t, e) {
    t.__proto__ = e;
  } || function (t, e) {
    for (var n in e) e.hasOwnProperty(n) && (t[n] = e[n]);
  }, R(t, e);
};
function F(t, e) {
  function n() {
    this.constructor = t;
  }
  R(t, e), t.prototype = null === e ? Object.create(e) : (n.prototype = e.prototype, new n());
}
var C = function () {
  return C = Object.assign || function (t) {
    for (var e, n = 1, r = arguments.length; n < r; n++) for (var i in e = arguments[n]) Object.prototype.hasOwnProperty.call(e, i) && (t[i] = e[i]);
    return t;
  }, C.apply(this, arguments);
};
function D(t, e, n, r) {
  return new (n || (n = Promise))(function (i, o) {
    function a(t) {
      try {
        u(r.next(t));
      } catch (t) {
        o(t);
      }
    }
    function s(t) {
      try {
        u(r.throw(t));
      } catch (t) {
        o(t);
      }
    }
    function u(t) {
      var e;
      t.done ? i(t.value) : (e = t.value, e instanceof n ? e : new n(function (t) {
        t(e);
      })).then(a, s);
    }
    u((r = r.apply(t, e || [])).next());
  });
}
function B(t, e) {
  var n,
    r,
    i,
    o,
    a = {
      label: 0,
      sent: function () {
        if (1 & i[0]) throw i[1];
        return i[1];
      },
      trys: [],
      ops: []
    };
  return o = {
    next: s(0),
    throw: s(1),
    return: s(2)
  }, "function" == typeof Symbol && (o[Symbol.iterator] = function () {
    return this;
  }), o;
  function s(o) {
    return function (s) {
      return function (o) {
        if (n) throw new TypeError("Generator is already executing.");
        for (; a;) try {
          if (n = 1, r && (i = 2 & o[0] ? r.return : o[0] ? r.throw || ((i = r.return) && i.call(r), 0) : r.next) && !(i = i.call(r, o[1])).done) return i;
          switch (r = 0, i && (o = [2 & o[0], i.value]), o[0]) {
            case 0:
            case 1:
              i = o;
              break;
            case 4:
              return a.label++, {
                value: o[1],
                done: !1
              };
            case 5:
              a.label++, r = o[1], o = [0];
              continue;
            case 7:
              o = a.ops.pop(), a.trys.pop();
              continue;
            default:
              if (!(i = a.trys, (i = i.length > 0 && i[i.length - 1]) || 6 !== o[0] && 2 !== o[0])) {
                a = 0;
                continue;
              }
              if (3 === o[0] && (!i || o[1] > i[0] && o[1] < i[3])) {
                a.label = o[1];
                break;
              }
              if (6 === o[0] && a.label < i[1]) {
                a.label = i[1], i = o;
                break;
              }
              if (i && a.label < i[2]) {
                a.label = i[2], a.ops.push(o);
                break;
              }
              i[2] && a.ops.pop(), a.trys.pop();
              continue;
          }
          o = e.call(t, a);
        } catch (t) {
          o = [6, t], r = 0;
        } finally {
          n = i = 0;
        }
        if (5 & o[0]) throw o[1];
        return {
          value: o[0] ? o[1] : void 0,
          done: !0
        };
      }([o, s]);
    };
  }
}
function L(t) {
  return t instanceof SVGAnimatedLength ? t.baseVal.value : t;
}
function z(t) {
  return D(this, void 0, void 0, function () {
    var r, i;
    return B(this, function (o) {
      switch (o.label) {
        case 0:
          return r = document.createElement("canvas"), t instanceof e ? [4, n.toPixels(t, r)] : [3, 2];
        case 1:
          return o.sent(), [3, 3];
        case 2:
          r.width = L(t.width), r.height = L(t.height), i = r.getContext("2d"), t instanceof ImageData ? i.putImageData(t, 0, 0) : i.drawImage(t, 0, 0), o.label = 3;
        case 3:
          return [2, r];
      }
    });
  });
}
function W(t) {
  return D(this, void 0, void 0, function () {
    var r, i, o, a, s, u;
    return B(this, function (f) {
      switch (f.label) {
        case 0:
          return t instanceof e ? (r = t.shape.slice(0, 2), i = r[0], o = r[1], a = ImageData.bind, [4, n.toPixels(t)]) : [3, 2];
        case 1:
          return [2, new (a.apply(ImageData, [void 0, f.sent(), o, i]))()];
        case 2:
          return s = document.createElement("canvas"), u = s.getContext("2d"), s.width = L(t.width), s.height = L(t.height), u.drawImage(t, 0, 0), [2, u.getImageData(0, 0, s.width, s.height)];
      }
    });
  });
}
function j(t) {
  return D(this, void 0, void 0, function () {
    var e, r;
    return B(this, function (i) {
      switch (i.label) {
        case 0:
          return t instanceof SVGImageElement || t instanceof OffscreenCanvas ? [4, z(t)] : [3, 2];
        case 1:
          return r = i.sent(), [3, 3];
        case 2:
          r = t, i.label = 3;
        case 3:
          return e = r, [2, n.fromPixels(e, 4)];
      }
    });
  });
}
function V(t) {
  if (t < 0 || t >= 256) throw new Error("Mask value must be in range [0, 255] but got " + t);
  if (!Number.isInteger(t)) throw new Error("Mask value must be an integer but got " + t);
}
var K = [[110, 64, 170], [143, 61, 178], [178, 60, 178], [210, 62, 167], [238, 67, 149], [255, 78, 125], [255, 94, 99], [255, 115, 75], [255, 140, 56], [239, 167, 47], [217, 194, 49], [194, 219, 64], [175, 240, 91], [135, 245, 87], [96, 247, 96], [64, 243, 115], [40, 234, 141], [28, 219, 169], [26, 199, 194], [33, 176, 213], [47, 150, 224], [65, 125, 224], [84, 101, 214], [99, 81, 195]];
function U(t) {
  if (V(t), t < K.length) {
    var e = K[t];
    return {
      r: e[0],
      g: e[1],
      b: e[2],
      a: 255
    };
  }
  throw new Error("Mask value must be in range [0, " + K.length + ")");
}
function q(t) {
  var e = t.shape[2],
    n = m(t, 2),
    r = c(n, [-1]);
  return s(r, e);
}
function N(t, e) {
  return r(function () {
    return i(o(t, a(e)), "int32");
  });
}
function Q(t, e) {
  var n = e.shape,
    o = n[0],
    s = n[1],
    m = n[2];
  return r(function () {
    var n = q(e),
      r = u(f(0, m, 1, "int32"), 1),
      g = i(l(n, r), "int32"),
      v = c(g, [o, s]),
      w = d(v, a(1, "int32"));
    return h(function (t, e) {
      return p(t, e);
    }(w, t), a(1, "int32"));
  });
}
var X = function () {
    function t(t, e) {
      this.model = t, this.outputStride = e;
      var n = this.model.inputs[0].shape;
      w.assert(-1 === n[1] && -1 === n[2], function () {
        return "Input shape [" + n[1] + ", " + n[2] + "] must both be equal to or -1";
      });
    }
    return t.prototype.predict = function (t) {
      var e = this;
      return r(function () {
        var n = e.preprocessInput(i(t, "float32")),
          r = u(n, 0),
          o = e.model.predict(r).map(function (t) {
            return g(t, [0]);
          }),
          a = e.nameOutputResults(o);
        return {
          heatmapScores: v(a.heatmap),
          offsets: a.offsets,
          displacementFwd: a.displacementFwd,
          displacementBwd: a.displacementBwd,
          segmentation: a.segmentation,
          partHeatmaps: a.partHeatmaps,
          longOffsets: a.longOffsets,
          partOffsets: a.partOffsets
        };
      });
    }, t.prototype.dispose = function () {
      this.model.dispose();
    }, t;
  }(),
  Y = function (t) {
    function e() {
      return null !== t && t.apply(this, arguments) || this;
    }
    return F(e, t), e.prototype.preprocessInput = function (t) {
      return r(function () {
        return h(y(t, 127.5), 1);
      });
    }, e.prototype.nameOutputResults = function (t) {
      return {
        offsets: t[0],
        segmentation: t[1],
        partHeatmaps: t[2],
        longOffsets: t[3],
        heatmap: t[4],
        displacementFwd: t[5],
        displacementBwd: t[6],
        partOffsets: t[7]
      };
    }, e;
  }(X),
  G = ["nose", "leftEye", "rightEye", "leftEar", "rightEar", "leftShoulder", "rightShoulder", "leftElbow", "rightElbow", "leftWrist", "rightWrist", "leftHip", "rightHip", "leftKnee", "rightKnee", "leftAnkle", "rightAnkle"],
  $ = G.length,
  J = G.reduce(function (t, e, n) {
    return t[e] = n, t;
  }, {});
[["leftHip", "leftShoulder"], ["leftElbow", "leftShoulder"], ["leftElbow", "leftWrist"], ["leftHip", "leftKnee"], ["leftKnee", "leftAnkle"], ["rightHip", "rightShoulder"], ["rightElbow", "rightShoulder"], ["rightElbow", "rightWrist"], ["rightHip", "rightKnee"], ["rightKnee", "rightAnkle"], ["leftShoulder", "rightShoulder"], ["leftHip", "rightHip"]].map(function (t) {
  var e = t[0],
    n = t[1];
  return [J[e], J[n]];
});
function Z(t, e, n) {
  var r = t[0],
    i = t[1],
    o = e[0],
    a = e[1],
    s = n.top,
    u = n.bottom;
  return [a / (n.left + n.right + i), o / (s + u + r)];
}
function tt(t, e, n, r) {
  return {
    y: r.get(t, e, n),
    x: r.get(t, e, n + $)
  };
}
function et(t, e, n) {
  var r = tt(t.heatmapY, t.heatmapX, t.id, n),
    i = r.y,
    o = r.x;
  return {
    x: t.heatmapX * e + o,
    y: t.heatmapY * e + i
  };
}
function nt(t, e, n) {
  return t < e ? e : t > n ? n : t;
}
function rt(t, e) {
  return {
    x: t.x + e.x,
    y: t.y + e.y
  };
}
function it(t, e, n) {
  void 0 === n && (n = .3);
  for (var r = 0, i = 0, o = 0; o < t.length; o++) e.keypoints[o].score > n && (i += 1, r += Math.pow(t[o].x - e.keypoints[o].position.x, 2) + Math.pow(t[o].y - e.keypoints[o].position.y, 2));
  return 0 === i ? r = 1 / 0 : r /= i, r;
}
function ot(t, e, n, r, i, o, a) {
  for (var s = a[0], u = a[1], f = n(t), l = f.y * r + f.x, c = i[$ * (2 * l) + e], d = i[$ * (2 * l + 1) + e], h = t.y + c, p = t.x + d, m = 0; m < o; m++) {
    h = Math.min(h, s - 1);
    var g = n({
        x: p = Math.min(p, u - 1),
        y: h
      }),
      v = g.y * r + g.x;
    h += c = i[$ * (2 * v) + e], p += d = i[$ * (2 * v + 1) + e];
  }
  return {
    x: p,
    y: h
  };
}
function at(t, e, n, r, i, o, a, s, u, f) {
  for (var l = i[0], c = i[1], d = o[0], h = o[1], p = s[0], m = s[1], g = [], v = function (t) {
      return function (t, e, n, r) {
        var i = e[0],
          o = e[1],
          a = n[0],
          s = n[1],
          u = Math.round(((i + t.y + 1) * s - 1) / r);
        return {
          x: Math.round(((o + t.x + 1) * a - 1) / r),
          y: u
        };
      }(t, [l, c], [d, h], u);
    }, w = 0; w < r; w++) {
    var y = ot(t, w, v, a, e, f, [p, m]);
    g.push(y);
  }
  for (var b = -1, S = 1 / 0, x = 0; x < n.length; x++) {
    var M = it(g, n[x]);
    M < S && (b = x, S = M);
  }
  return b;
}
function st(t, e) {
  var n = t[0],
    r = t[1];
  return [Math.round((r - 1) / e + 1), Math.round((n - 1) / e + 1)];
}
function ut(t, e, n, r, i, o, a, s, u, f, l) {
  for (var d = a[0], h = a[1], p = t.shape, m = p[0], g = p[1], v = e.shape.slice(0, 2), w = v[0], y = v[1], x = c(e, [w, y, 2, $]), M = new Float32Array(l * $ * 3).fill(0), k = 0; k < n.length; k++) for (var E = k * $ * 3, T = n[k], P = 0; P < $; P++) {
    var I = T.keypoints[P],
      O = E + 3 * P;
    M[O] = I.score, M[O + 1] = I.position.y, M[O + 2] = I.position.x;
  }
  var _ = Z([r, i], [d, h], s),
    H = _[0],
    A = _[1],
    R = b(M, [l, $, 3]),
    F = s.top,
    C = s.left,
    D = {
      variableNames: ["segmentation", "longOffsets", "poses"],
      outputShape: [m, g],
      userCode: "\n    int convertToPositionInOutput(int pos, int pad, float scale, int stride) {\n      return round(((float(pos + pad) + 1.0) * scale - 1.0) / float(stride));\n    }\n\n    float convertToPositionInOutputFloat(\n        int pos, int pad, float scale, int stride) {\n      return ((float(pos + pad) + 1.0) * scale - 1.0) / float(stride);\n    }\n\n    float dist(float x1, float y1, float x2, float y2) {\n      return pow(x1 - x2, 2.0) + pow(y1 - y2, 2.0);\n    }\n\n    float sampleLongOffsets(float h, float w, int d, int k) {\n      float fh = fract(h);\n      float fw = fract(w);\n      int clH = int(ceil(h));\n      int clW = int(ceil(w));\n      int flH = int(floor(h));\n      int flW = int(floor(w));\n      float o11 = getLongOffsets(flH, flW, d, k);\n      float o12 = getLongOffsets(flH, clW, d, k);\n      float o21 = getLongOffsets(clH, flW, d, k);\n      float o22 = getLongOffsets(clH, clW, d, k);\n      float o1 = mix(o11, o12, fw);\n      float o2 = mix(o21, o22, fw);\n      return mix(o1, o2, fh);\n    }\n\n    int findNearestPose(int h, int w) {\n      float prob = getSegmentation(h, w);\n      if (prob < 1.0) {\n        return -1;\n      }\n\n      // Done(Tyler): convert from output space h/w to strided space.\n      float stridedH = convertToPositionInOutputFloat(\n        h, " + F + ", " + A + ", " + o + ");\n      float stridedW = convertToPositionInOutputFloat(\n        w, " + C + ", " + H + ", " + o + ");\n\n      float minDist = 1000000.0;\n      int iMin = -1;\n      for (int i = 0; i < " + l + "; i++) {\n        float curDistSum = 0.0;\n        int numKpt = 0;\n        for (int k = 0; k < " + $ + "; k++) {\n          float dy = sampleLongOffsets(stridedH, stridedW, 0, k);\n          float dx = sampleLongOffsets(stridedH, stridedW, 1, k);\n\n          float y = float(h) + dy;\n          float x = float(w) + dx;\n\n          for (int s = 0; s < " + u + "; s++) {\n            int yRounded = round(min(y, float(" + (r - 1) + ")));\n            int xRounded = round(min(x, float(" + (i - 1) + ")));\n\n            float yStrided = convertToPositionInOutputFloat(\n              yRounded, " + F + ", " + A + ", " + o + ");\n            float xStrided = convertToPositionInOutputFloat(\n              xRounded, " + C + ", " + H + ", " + o + ");\n\n            float dy = sampleLongOffsets(yStrided, xStrided, 0, k);\n            float dx = sampleLongOffsets(yStrided, xStrided, 1, k);\n\n            y = y + dy;\n            x = x + dx;\n          }\n\n          float poseScore = getPoses(i, k, 0);\n          float poseY = getPoses(i, k, 1);\n          float poseX = getPoses(i, k, 2);\n          if (poseScore > " + f + ") {\n            numKpt = numKpt + 1;\n            curDistSum = curDistSum + dist(x, y, poseX, poseY);\n          }\n        }\n        if (numKpt > 0 && curDistSum / float(numKpt) < minDist) {\n          minDist = curDistSum / float(numKpt);\n          iMin = i;\n        }\n      }\n      return iMin;\n    }\n\n    void main() {\n        ivec2 coords = getOutputCoords();\n        int nearestPose = findNearestPose(coords[0], coords[1]);\n        setOutput(float(nearestPose));\n      }\n  "
    };
  return S().compileAndRun(D, [t, x, R]);
}
function ft() {
  return "webgl" === x();
}
function lt(t, e, n, o, s, u, f, l, c, d, h, p) {
  var m = f[0],
    g = f[1];
  return void 0 === c && (c = .2), void 0 === d && (d = 8), void 0 === h && (h = .3), void 0 === p && (p = 10), D(this, void 0, void 0, function () {
    var f, v, w, y, b;
    return B(this, function (S) {
      switch (S.label) {
        case 0:
          return f = n.filter(function (t) {
            return t.score >= c;
          }), ft() ? (w = r(function () {
            var n = ut(t, e, f, o, s, u, [m, g], l, d, h, p),
              c = M().makeTensorFromDataId(n.dataId, n.shape, n.dtype);
            return f.map(function (t, e) {
              return function (t, e) {
                return r(function () {
                  return i(k(t, a(e)), "int32");
                });
              }(c, e);
            });
          }), [4, Promise.all(w.map(function (t) {
            return t.data();
          }))]) : [3, 2];
        case 1:
          return v = S.sent(), w.forEach(function (t) {
            return t.dispose();
          }), [3, 5];
        case 2:
          return [4, t.data()];
        case 3:
          return y = S.sent(), [4, e.data()];
        case 4:
          b = S.sent(), v = function (t, e, n, r, i, o, a, s, u, f) {
            var l = a[0],
              c = a[1];
            void 0 === f && (f = 5);
            for (var d = n.map(function (t) {
                return new Uint8Array(r * i).fill(0);
              }), h = s.top, p = s.left, m = Z([r, i], [l, c], s), g = m[0], v = m[1], w = st([l, c], o)[0], y = 0; y < r; y += 1) for (var b = 0; b < i; b += 1) {
              var S = y * i + b;
              if (1 === t[S]) {
                var x = at({
                  x: b,
                  y: y
                }, e, n, f, [h, p], [g, v], w, [r, i], o, u);
                x >= 0 && (d[x][S] = 1);
              }
            }
            return d;
          }(y, b, f, o, s, u, [m, g], l, d), S.label = 5;
        case 5:
          return [2, v.map(function (t, e) {
            return {
              data: t,
              pose: f[e],
              width: s,
              height: o
            };
          })];
      }
    });
  });
}
function ct(t, e, n, o, s, u, f, l, c, m, g, v, w) {
  var y = l[0],
    b = l[1];
  return void 0 === m && (m = .2), void 0 === g && (g = 8), void 0 === v && (v = .3), void 0 === w && (w = 10), D(this, void 0, void 0, function () {
    var l, S, x, E, T, P;
    return B(this, function (I) {
      switch (I.label) {
        case 0:
          return l = o.filter(function (t) {
            return t.score >= m;
          }), ft() ? (x = r(function () {
            var o = ut(t, e, l, s, u, f, [y, b], c, g, v, w),
              m = M().makeTensorFromDataId(o.dataId, o.shape, o.dtype);
            return l.map(function (t, e) {
              return function (t, e, n) {
                return r(function () {
                  return h(p(i(k(t, a(n)), "int32"), d(e, 1)), 1);
                });
              }(m, n, e);
            });
          }), [4, Promise.all(x.map(function (t) {
            return t.data();
          }))]) : [3, 2];
        case 1:
          return S = I.sent(), x.forEach(function (t) {
            return t.dispose();
          }), [3, 6];
        case 2:
          return [4, t.data()];
        case 3:
          return E = I.sent(), [4, e.data()];
        case 4:
          return T = I.sent(), [4, n.data()];
        case 5:
          P = I.sent(), S = function (t, e, n, r, i, o, a, s, u, f, l) {
            var c = s[0],
              d = s[1];
            void 0 === l && (l = 5);
            for (var h = r.map(function (t) {
                return new Int32Array(i * o).fill(-1);
              }), p = u.top, m = u.left, g = Z([i, o], [c, d], u), v = g[0], w = g[1], y = st([c, d], a)[0], b = 0; b < i; b += 1) for (var S = 0; S < o; S += 1) {
              var x = b * o + S;
              if (1 === t[x]) {
                var M = at({
                  x: S,
                  y: b
                }, e, r, l, [p, m], [v, w], y, [i, o], a, f);
                M >= 0 && (h[M][x] = n[x]);
              }
            }
            return h;
          }(E, T, P, l, s, u, f, [y, b], c, g), I.label = 6;
        case 6:
          return [2, S.map(function (t, e) {
            return {
              pose: l[e],
              data: t,
              height: s,
              width: u
            };
          })];
      }
    });
  });
}
function dt(t) {
  return Math.floor(t / 2);
}
var ht = function () {
  function t(t, e) {
    this.priorityQueue = new Array(t), this.numberOfElements = -1, this.getElementValue = e;
  }
  return t.prototype.enqueue = function (t) {
    this.priorityQueue[++this.numberOfElements] = t, this.swim(this.numberOfElements);
  }, t.prototype.dequeue = function () {
    var t = this.priorityQueue[0];
    return this.exchange(0, this.numberOfElements--), this.sink(0), this.priorityQueue[this.numberOfElements + 1] = null, t;
  }, t.prototype.empty = function () {
    return -1 === this.numberOfElements;
  }, t.prototype.size = function () {
    return this.numberOfElements + 1;
  }, t.prototype.all = function () {
    return this.priorityQueue.slice(0, this.numberOfElements + 1);
  }, t.prototype.max = function () {
    return this.priorityQueue[0];
  }, t.prototype.swim = function (t) {
    for (; t > 0 && this.less(dt(t), t);) this.exchange(t, dt(t)), t = dt(t);
  }, t.prototype.sink = function (t) {
    for (; 2 * t <= this.numberOfElements;) {
      var e = 2 * t;
      if (e < this.numberOfElements && this.less(e, e + 1) && e++, !this.less(t, e)) break;
      this.exchange(t, e), t = e;
    }
  }, t.prototype.getValueAt = function (t) {
    return this.getElementValue(this.priorityQueue[t]);
  }, t.prototype.less = function (t, e) {
    return this.getValueAt(t) < this.getValueAt(e);
  }, t.prototype.exchange = function (t, e) {
    var n = this.priorityQueue[t];
    this.priorityQueue[t] = this.priorityQueue[e], this.priorityQueue[e] = n;
  }, t;
}();
function pt(t, e, n, r, i, o) {
  for (var a = o.shape, s = a[0], u = a[1], f = !0, l = Math.max(n - i, 0), c = Math.min(n + i + 1, s), d = l; d < c; ++d) {
    for (var h = Math.max(r - i, 0), p = Math.min(r + i + 1, u), m = h; m < p; ++m) if (o.get(d, m, t) > e) {
      f = !1;
      break;
    }
    if (!f) break;
  }
  return f;
}
var mt = [["nose", "leftEye"], ["leftEye", "leftEar"], ["nose", "rightEye"], ["rightEye", "rightEar"], ["nose", "leftShoulder"], ["leftShoulder", "leftElbow"], ["leftElbow", "leftWrist"], ["leftShoulder", "leftHip"], ["leftHip", "leftKnee"], ["leftKnee", "leftAnkle"], ["nose", "rightShoulder"], ["rightShoulder", "rightElbow"], ["rightElbow", "rightWrist"], ["rightShoulder", "rightHip"], ["rightHip", "rightKnee"], ["rightKnee", "rightAnkle"]].map(function (t) {
    var e = t[0],
      n = t[1];
    return [J[e], J[n]];
  }),
  gt = mt.map(function (t) {
    return t[1];
  }),
  vt = mt.map(function (t) {
    return t[0];
  });
function wt(t, e, n, r) {
  return {
    y: nt(Math.round(t.y / e), 0, n - 1),
    x: nt(Math.round(t.x / e), 0, r - 1)
  };
}
function yt(t, e, n, r, i, o, a, s) {
  void 0 === s && (s = 2);
  for (var u = r.shape, f = u[0], l = u[1], c = function (t, e, n) {
      var r = n.shape[2] / 2;
      return {
        y: n.get(e.y, e.x, t),
        x: n.get(e.y, e.x, r + t)
      };
    }(t, wt(e.position, o, f, l), a), d = rt(e.position, c), h = 0; h < s; h++) {
    var p = wt(d, o, f, l),
      m = tt(p.y, p.x, n, i);
    d = rt({
      x: p.x * o,
      y: p.y * o
    }, {
      x: m.x,
      y: m.y
    });
  }
  var g = wt(d, o, f, l),
    v = r.get(g.y, g.x, n);
  return {
    position: d,
    part: G[n],
    score: v
  };
}
function bt(t, e, n, r, i, o) {
  var a = e.shape[2],
    s = gt.length,
    u = new Array(a),
    f = t.part,
    l = t.score,
    c = et(f, r, n);
  u[f.id] = {
    score: l,
    part: G[f.id],
    position: c
  };
  for (var d = s - 1; d >= 0; --d) {
    var h = gt[d],
      p = vt[d];
    u[h] && !u[p] && (u[p] = yt(d, u[h], p, e, n, r, o));
  }
  for (d = 0; d < s; ++d) {
    h = vt[d], p = gt[d];
    u[h] && !u[p] && (u[p] = yt(d, u[h], p, e, n, r, i));
  }
  return u;
}
function St(t, e, n, r) {
  var i = n.x,
    o = n.y;
  return t.some(function (t) {
    var n,
      a,
      s,
      u,
      f,
      l,
      c = t.keypoints[r].position;
    return n = o, a = i, s = c.y, u = c.x, (f = s - n) * f + (l = u - a) * l <= e;
  });
}
function xt(t, e, n) {
  var r = n.reduce(function (n, r, i) {
    var o = r.position,
      a = r.score;
    return St(t, e, o, i) || (n += a), n;
  }, 0);
  return r / n.length;
}
function Mt(t, e, n, r, i, o, a, s) {
  void 0 === a && (a = .5), void 0 === s && (s = 20);
  for (var u = [], f = function (t, e, n) {
      for (var r = n.shape, i = r[0], o = r[1], a = r[2], s = new ht(i * o * a, function (t) {
          return t.score;
        }), u = 0; u < i; ++u) for (var f = 0; f < o; ++f) for (var l = 0; l < a; ++l) {
        var c = n.get(u, f, l);
        c < t || pt(l, c, u, f, e, n) && s.enqueue({
          score: c,
          part: {
            heatmapY: u,
            heatmapX: f,
            id: l
          }
        });
      }
      return s;
    }(a, 1, t), l = s * s; u.length < o && !f.empty();) {
    var c = f.dequeue();
    if (!St(u, l, et(c.part, i, e), c.part.id)) {
      var d = bt(c, t, e, i, n, r),
        h = xt(u, l, d);
      u.push({
        keypoints: d,
        score: h
      });
    }
  }
  return u;
}
var kt,
  Et = [-123.15, -115.9, -103.06],
  Tt = function (t) {
    function e() {
      return null !== t && t.apply(this, arguments) || this;
    }
    return F(e, t), e.prototype.preprocessInput = function (t) {
      return d(t, Et);
    }, e.prototype.nameOutputResults = function (t) {
      var e = t[0],
        n = t[1],
        r = t[2],
        i = t[3],
        o = t[4],
        a = t[5];
      return {
        offsets: o,
        segmentation: t[6],
        partHeatmaps: a,
        longOffsets: i,
        heatmap: r,
        displacementFwd: n,
        displacementBwd: e,
        partOffsets: t[7]
      };
    }, e;
  }(X),
  Pt = "https://storage.googleapis.com/tfjs-models/savedmodel/bodypix/resnet50/",
  It = "https://storage.googleapis.com/tfjs-models/savedmodel/bodypix/mobilenet/";
function Ot(t) {
  if ("undefined" != typeof HTMLCanvasElement && t instanceof HTMLCanvasElement || "undefined" != typeof OffscreenCanvas && t instanceof OffscreenCanvas || "undefined" != typeof HTMLImageElement && t instanceof HTMLImageElement) return function (t) {
    if ("offsetHeight" in t && 0 !== t.offsetHeight && "offsetWidth" in t && 0 !== t.offsetWidth) return [t.offsetHeight, t.offsetWidth];
    if (null != t.height && null != t.width) return [t.height, t.width];
    throw new Error("HTMLImageElement must have height and width attributes set.");
  }(t);
  if ("undefined" != typeof ImageData && t instanceof ImageData) return [t.height, t.width];
  if ("undefined" != typeof HTMLVideoElement && t instanceof HTMLVideoElement) return function (t) {
    return t.hasAttribute("height") && t.hasAttribute("width") ? [t.height, t.width] : [t.videoHeight, t.videoWidth];
  }(t);
  if (t instanceof e) return [t.shape[0], t.shape[1]];
  throw new Error("error: Unknown input type: " + t + ".");
}
function _t(t, e) {
  return function (t, e) {
    return (t - 1) % e == 0;
  }(t, e) ? t : Math.floor(t / e) * e + 1;
}
var Ht = {
    low: "low",
    medium: "medium",
    high: "high",
    full: "full"
  },
  At = ((kt = {})[Ht.low] = .25, kt[Ht.medium] = .5, kt[Ht.high] = .75, kt[Ht.full] = 1, kt);
function Rt(t, e, n) {
  var r = n[0],
    i = n[1],
    o = function (t) {
      if ("string" == typeof t) {
        var e = At[t];
        return w.assert("number" == typeof e, function () {
          return "string value of inputResolution must be one of " + Object.values(Ht).join(",") + " but was " + t + ".";
        }), e;
      }
      return w.assert("number" == typeof t && t <= 2 && t >= .1, function () {
        return "inputResolution must be a string or number between 0.1 and 2, but was " + t;
      }), t;
    }(t);
  return [_t(r * o, e), _t(i * o, e)];
}
function Ft(t, e, n, i, o) {
  var a = e[0],
    s = e[1],
    f = n[0],
    l = n[1],
    c = i[0],
    d = c[0],
    h = c[1],
    p = i[1],
    m = p[0],
    w = p[1];
  return void 0 === o && (o = !1), r(function () {
    var e = E.resizeBilinear(t, [f, l], !0);
    return o && (e = v(e)), function (t, e, n) {
      var i = e[0],
        o = e[1],
        a = n[0],
        s = a[0],
        f = a[1],
        l = n[1],
        c = l[0],
        d = l[1];
      return r(function () {
        var e = u(t);
        return g(E.cropAndResize(e, [[s / (i + s + f - 1), c / (o + c + d - 1), (s + i - 1) / (i + s + f - 1), (c + o - 1) / (o + c + d - 1)]], [0], [i, o]), [0]);
      });
    }(e, [a, s], [[d, h], [m, w]]);
  });
}
function Ct(t, i) {
  var o = i[0],
    a = i[1],
    s = Ot(t),
    u = s[0],
    f = s[1],
    l = a / o,
    c = [0, 0, 0, 0],
    d = c[0],
    h = c[1],
    p = c[2],
    m = c[3];
  f / u < l ? (d = 0, h = 0, p = Math.round(.5 * (l * u - f)), m = Math.round(.5 * (l * u - f))) : (d = Math.round(.5 * (1 / l * f - u)), h = Math.round(.5 * (1 / l * f - u)), p = 0, m = 0);
  var g = r(function () {
    var r = function (t) {
      return t instanceof e ? t : n.fromPixels(t);
    }(t);
    return r = T(r, [[d, h], [p, m], [0, 0]]), E.resizeBilinear(r, [o, a]);
  });
  return {
    resized: g,
    padding: {
      top: d,
      left: p,
      right: m,
      bottom: h
    }
  };
}
function Dt(t) {
  return D(this, void 0, void 0, function () {
    return B(this, function (e) {
      return [2, Promise.all(t.map(function (t) {
        return t.buffer();
      }))];
    });
  });
}
function Bt(t, e, n, r, i) {
  var o = e[0],
    a = e[1],
    s = n[0],
    u = n[1],
    f = function (t, e, n, r, i) {
      return void 0 === r && (r = 0), void 0 === i && (i = 0), 1 === n && 1 === e && 0 === r && 0 === i ? t : t.map(function (t) {
        return function (t, e, n, r, i) {
          return void 0 === r && (r = 0), void 0 === i && (i = 0), {
            score: t.score,
            keypoints: t.keypoints.map(function (t) {
              var o = t.score,
                a = t.part,
                s = t.position;
              return {
                score: o,
                part: a,
                position: {
                  x: s.x * n + i,
                  y: s.y * e + r
                }
              };
            })
          };
        }(t, e, n, r, i);
      });
    }(t, (o + r.top + r.bottom) / s, (a + r.left + r.right) / u, -r.top, -r.left);
  return i ? function (t, e) {
    return e <= 0 ? t : t.map(function (t) {
      return function (t, e) {
        return {
          score: t.score,
          keypoints: t.keypoints.map(function (t) {
            var n = t.score,
              r = t.part,
              i = t.position;
            return {
              score: n,
              part: r,
              position: {
                x: e - 1 - i.x,
                y: i.y
              }
            };
          })
        };
      }(t, e);
    });
  }(f, a) : f;
}
var Lt = {
    architecture: "MobileNetV1",
    outputStride: 16,
    quantBytes: 4,
    multiplier: .75
  },
  zt = ["MobileNetV1", "ResNet50"],
  Wt = {
    MobileNetV1: [8, 16, 32],
    ResNet50: [32, 16]
  },
  jt = {
    MobileNetV1: [.5, .75, 1],
    ResNet50: [1]
  },
  Vt = [1, 2, 4];
var Kt = {
    flipHorizontal: !1,
    internalResolution: "medium",
    segmentationThreshold: .7,
    maxDetections: 10,
    scoreThreshold: .4,
    nmsRadius: 20
  },
  Ut = {
    flipHorizontal: !1,
    internalResolution: "medium",
    segmentationThreshold: .7,
    maxDetections: 10,
    scoreThreshold: .4,
    nmsRadius: 20,
    minKeypointScore: .3,
    refineSteps: 10
  };
function qt(t) {
  var e = t.segmentationThreshold,
    n = t.maxDetections,
    r = t.scoreThreshold,
    i = t.nmsRadius;
  if (e < 0 || e > 1) throw new Error("segmentationThreshold " + e + ". Should be in range [0.0, 1.0]");
  if (n <= 0) throw new Error("Invalid maxDetections " + n + ". Should be > 0");
  if (r < 0 || r > 1) throw new Error("Invalid scoreThreshold " + r + ". Should be in range [0.0, 1.0]");
  if (i <= 0) throw new Error("Invalid nmsRadius " + i + ".");
}
function Nt(t) {
  var e = t.segmentationThreshold,
    n = t.maxDetections,
    r = t.scoreThreshold,
    i = t.nmsRadius,
    o = t.minKeypointScore,
    a = t.refineSteps;
  if (e < 0 || e > 1) throw new Error("segmentationThreshold " + e + ". Should be in range [0.0, 1.0]");
  if (n <= 0) throw new Error("Invalid maxDetections " + n + ". Should be > 0");
  if (r < 0 || r > 1) throw new Error("Invalid scoreThreshold " + r + ". Should be in range [0.0, 1.0]");
  if (i <= 0) throw new Error("Invalid nmsRadius " + i + ".");
  if (o < 0 || o > 1) throw new Error("Invalid minKeypointScore " + o + ".Should be in range [0.0, 1.0]");
  if (a <= 0 || a > 20) throw new Error("Invalid refineSteps " + a + ".Should be in range [1, 20]");
}
var Qt = function () {
  function t(t) {
    this.baseModel = t;
  }
  return t.prototype.predictForPersonSegmentation = function (t) {
    var e = this.baseModel.predict(t);
    return {
      segmentLogits: e.segmentation,
      heatmapScores: e.heatmapScores,
      offsets: e.offsets,
      displacementFwd: e.displacementFwd,
      displacementBwd: e.displacementBwd
    };
  }, t.prototype.predictForPersonSegmentationAndPart = function (t) {
    var e = this.baseModel.predict(t);
    return {
      segmentLogits: e.segmentation,
      partHeatmapLogits: e.partHeatmaps,
      heatmapScores: e.heatmapScores,
      offsets: e.offsets,
      displacementFwd: e.displacementFwd,
      displacementBwd: e.displacementBwd
    };
  }, t.prototype.predictForMultiPersonInstanceSegmentationAndPart = function (t) {
    var e = this.baseModel.predict(t);
    return {
      segmentLogits: e.segmentation,
      longOffsets: e.longOffsets,
      heatmapScores: e.heatmapScores,
      offsets: e.offsets,
      displacementFwd: e.displacementFwd,
      displacementBwd: e.displacementBwd,
      partHeatmaps: e.partHeatmaps
    };
  }, t.prototype.segmentPersonActivation = function (t, e, n) {
    var i = this;
    void 0 === n && (n = .5);
    var o = Ot(t),
      a = o[0],
      s = o[1],
      u = Rt(e, this.baseModel.outputStride, [a, s]),
      f = Ct(t, u),
      l = f.resized,
      c = f.padding,
      d = r(function () {
        var t = i.predictForPersonSegmentation(l),
          e = t.segmentLogits,
          r = t.heatmapScores,
          o = t.offsets,
          u = t.displacementFwd,
          f = t.displacementBwd,
          d = l.shape,
          h = d[0],
          p = d[1],
          m = Ft(e, [a, s], [h, p], [[c.top, c.bottom], [c.left, c.right]], true);
        return {
          segmentation: N(g(m), n),
          heatmapScores: r,
          offsets: o,
          displacementFwd: u,
          displacementBwd: f
        };
      }),
      h = d.segmentation,
      p = d.heatmapScores,
      m = d.offsets,
      v = d.displacementFwd,
      w = d.displacementBwd;
    return l.dispose(), {
      segmentation: h,
      heatmapScores: p,
      offsets: m,
      displacementFwd: v,
      displacementBwd: w,
      padding: c,
      internalResolutionHeightAndWidth: u
    };
  }, t.prototype.segmentPerson = function (t, e) {
    return void 0 === e && (e = Kt), D(this, void 0, void 0, function () {
      var n, r, i, o, a, s, u, f, l, c, d, h, p, m, g, v, w, y;
      return B(this, function (b) {
        switch (b.label) {
          case 0:
            return qt(e = C(C({}, Kt), e)), n = this.segmentPersonActivation(t, e.internalResolution, e.segmentationThreshold), r = n.segmentation, i = n.heatmapScores, o = n.offsets, a = n.displacementFwd, s = n.displacementBwd, u = n.padding, f = n.internalResolutionHeightAndWidth, l = r.shape, c = l[0], d = l[1], [4, r.data()];
          case 1:
            return h = b.sent(), r.dispose(), [4, Dt([i, o, a, s])];
          case 2:
            return p = b.sent(), m = p[0], g = p[1], v = p[2], w = p[3], y = Bt(y = Mt(m, g, v, w, this.baseModel.outputStride, e.maxDetections, e.scoreThreshold, e.nmsRadius), [c, d], f, u, false), i.dispose(), o.dispose(), a.dispose(), s.dispose(), [2, {
              height: c,
              width: d,
              data: h,
              allPoses: y
            }];
        }
      });
    });
  }, t.prototype.segmentMultiPerson = function (t, e) {
    return void 0 === e && (e = Ut), D(this, void 0, void 0, function () {
      var n,
        i,
        o,
        a,
        s,
        u,
        f,
        l,
        c,
        d,
        h,
        p,
        m,
        v,
        w,
        y,
        b,
        S,
        x,
        M,
        k,
        E = this;
      return B(this, function (T) {
        switch (T.label) {
          case 0:
            return Nt(e = C(C({}, Ut), e)), n = Ot(t), i = n[0], o = n[1], a = Rt(e.internalResolution, this.baseModel.outputStride, [i, o]), s = Ct(t, a), u = s.resized, f = s.padding, l = r(function () {
              var t,
                n = E.predictForMultiPersonInstanceSegmentationAndPart(u),
                r = n.segmentLogits,
                s = n.longOffsets,
                l = n.heatmapScores,
                c = n.offsets,
                d = n.displacementFwd,
                h = n.displacementBwd,
                p = Ft(r, [i, o], a, [[f.top, f.bottom], [f.left, f.right]], true);
              return t = s, {
                segmentation: N(g(p), e.segmentationThreshold),
                longOffsets: t,
                heatmapScoresRaw: l,
                offsetsRaw: c,
                displacementFwdRaw: d,
                displacementBwdRaw: h
              };
            }), c = l.segmentation, d = l.longOffsets, h = l.heatmapScoresRaw, p = l.offsetsRaw, m = l.displacementFwdRaw, v = l.displacementBwdRaw, [4, Dt([h, p, m, v])];
          case 1:
            return w = T.sent(), y = w[0], b = w[1], S = w[2], x = w[3], M = Bt(M = Mt(y, b, S, x, this.baseModel.outputStride, e.maxDetections, e.scoreThreshold, e.nmsRadius), [i, o], a, f, false), [4, lt(c, d, M, i, o, this.baseModel.outputStride, a, f, e.scoreThreshold, e.refineSteps, e.minKeypointScore, e.maxDetections)];
          case 2:
            return k = T.sent(), u.dispose(), c.dispose(), d.dispose(), h.dispose(), p.dispose(), m.dispose(), v.dispose(), [2, k];
        }
      });
    });
  }, t.prototype.segmentPersonPartsActivation = function (t, e, n) {
    var i = this;
    void 0 === n && (n = .5);
    var o = Ot(t),
      a = o[0],
      s = o[1],
      u = Rt(e, this.baseModel.outputStride, [a, s]),
      f = Ct(t, u),
      l = f.resized,
      c = f.padding,
      d = r(function () {
        var t = i.predictForPersonSegmentationAndPart(l),
          e = t.segmentLogits,
          r = t.partHeatmapLogits,
          o = t.heatmapScores,
          u = t.offsets,
          f = t.displacementFwd,
          d = t.displacementBwd,
          h = l.shape,
          p = h[0],
          m = h[1],
          v = Ft(e, [a, s], [p, m], [[c.top, c.bottom], [c.left, c.right]], true),
          w = Ft(r, [a, s], [p, m], [[c.top, c.bottom], [c.left, c.right]], true);
        return {
          partSegmentation: Q(N(g(v), n), w),
          heatmapScores: o,
          offsets: u,
          displacementFwd: f,
          displacementBwd: d
        };
      }),
      h = d.partSegmentation,
      p = d.heatmapScores,
      m = d.offsets,
      v = d.displacementFwd,
      w = d.displacementBwd;
    return l.dispose(), {
      partSegmentation: h,
      heatmapScores: p,
      offsets: m,
      displacementFwd: v,
      displacementBwd: w,
      padding: c,
      internalResolutionHeightAndWidth: u
    };
  }, t.prototype.segmentPersonParts = function (t, e) {
    return void 0 === e && (e = Kt), D(this, void 0, void 0, function () {
      var n, r, i, o, a, s, u, f, l, c, d, h, p, m, g, v, w, y;
      return B(this, function (b) {
        switch (b.label) {
          case 0:
            return qt(e = C(C({}, Kt), e)), n = this.segmentPersonPartsActivation(t, e.internalResolution, e.segmentationThreshold), r = n.partSegmentation, i = n.heatmapScores, o = n.offsets, a = n.displacementFwd, s = n.displacementBwd, u = n.padding, f = n.internalResolutionHeightAndWidth, l = r.shape, c = l[0], d = l[1], [4, r.data()];
          case 1:
            return h = b.sent(), r.dispose(), [4, Dt([i, o, a, s])];
          case 2:
            return p = b.sent(), m = p[0], g = p[1], v = p[2], w = p[3], y = Bt(y = Mt(m, g, v, w, this.baseModel.outputStride, e.maxDetections, e.scoreThreshold, e.nmsRadius), [c, d], f, u, false), i.dispose(), o.dispose(), a.dispose(), s.dispose(), [2, {
              height: c,
              width: d,
              data: h,
              allPoses: y
            }];
        }
      });
    });
  }, t.prototype.segmentMultiPersonParts = function (t, e) {
    return void 0 === e && (e = Ut), D(this, void 0, void 0, function () {
      var n,
        o,
        a,
        s,
        d,
        h,
        p,
        m,
        v,
        w,
        y,
        b,
        S,
        x,
        M,
        k,
        E,
        T,
        P,
        I,
        O,
        _,
        H = this;
      return B(this, function (A) {
        switch (A.label) {
          case 0:
            return Nt(e = C(C({}, Ut), e)), n = Ot(t), o = n[0], a = n[1], s = Rt(e.internalResolution, this.baseModel.outputStride, [o, a]), d = Ct(t, s), h = d.resized, p = d.padding, m = r(function () {
              var t = H.predictForMultiPersonInstanceSegmentationAndPart(h),
                n = t.segmentLogits,
                d = t.longOffsets,
                m = t.heatmapScores,
                v = t.offsets,
                w = t.displacementFwd,
                y = t.displacementBwd,
                b = t.partHeatmaps,
                S = Ft(n, [o, a], s, [[p.top, p.bottom], [p.left, p.right]], true),
                x = Ft(b, [o, a], s, [[p.top, p.bottom], [p.left, p.right]], true),
                M = d,
                k = N(g(S), e.segmentationThreshold),
                E = function (t) {
                  var e = t.shape,
                    n = e[0],
                    o = e[1],
                    a = e[2];
                  return r(function () {
                    var e = q(t),
                      r = u(f(0, a, 1, "int32"), 1),
                      s = i(l(e, r), "int32");
                    return c(s, [n, o]);
                  });
                }(x);
              return {
                segmentation: k,
                longOffsets: M,
                heatmapScoresRaw: m,
                offsetsRaw: v,
                displacementFwdRaw: w,
                displacementBwdRaw: y,
                partSegmentation: E
              };
            }), v = m.segmentation, w = m.longOffsets, y = m.heatmapScoresRaw, b = m.offsetsRaw, S = m.displacementFwdRaw, x = m.displacementBwdRaw, M = m.partSegmentation, [4, Dt([y, b, S, x])];
          case 1:
            return k = A.sent(), E = k[0], T = k[1], P = k[2], I = k[3], O = Bt(O = Mt(E, T, P, I, this.baseModel.outputStride, e.maxDetections, e.scoreThreshold, e.nmsRadius), [o, a], s, p, false), [4, ct(v, w, M, O, o, a, this.baseModel.outputStride, s, p, e.scoreThreshold, e.refineSteps, e.minKeypointScore, e.maxDetections)];
          case 2:
            return _ = A.sent(), h.dispose(), v.dispose(), w.dispose(), y.dispose(), b.dispose(), S.dispose(), x.dispose(), M.dispose(), [2, _];
        }
      });
    });
  }, t.prototype.dispose = function () {
    this.baseModel.dispose();
  }, t;
}();
function Xt(e) {
  return D(this, void 0, void 0, function () {
    var n, r, i, o, a, s;
    return B(this, function (u) {
      switch (u.label) {
        case 0:
          if (n = e.outputStride, r = e.quantBytes, i = e.multiplier, null == t) throw new Error("Cannot find TensorFlow.js. If you are using a <script> tag, please also include @tensorflow/tfjs on the page before using this\n        model.");
          return o = function (t, e, n) {
            var r = {
                1: "100",
                .75: "075",
                .5: "050"
              },
              i = "model-stride" + t + ".json";
            return 4 === n ? It + "float/" + r[e] + "/" + i : It + "quant" + n + "/" + r[e] + "/" + i;
          }(n, i, r), [4, H(e.modelUrl || o)];
        case 1:
          return a = u.sent(), s = new Y(a, n), [2, new Qt(s)];
      }
    });
  });
}
function Yt(e) {
  return D(this, void 0, void 0, function () {
    var n, r, i, o, a;
    return B(this, function (s) {
      switch (s.label) {
        case 0:
          if (n = e.outputStride, r = e.quantBytes, null == t) throw new Error("Cannot find TensorFlow.js. If you are using a <script> tag, please also include @tensorflow/tfjs on the page before using this\n        model.");
          return i = function (t, e) {
            var n = "model-stride" + t + ".json";
            return 4 === e ? Pt + "float/" + n : Pt + "quant" + e + "/" + n;
          }(n, r), [4, H(e.modelUrl || i)];
        case 1:
          return o = s.sent(), a = new Tt(o, n), [2, new Qt(a)];
      }
    });
  });
}
function Gt(t) {
  return void 0 === t && (t = Lt), D(this, void 0, void 0, function () {
    return B(this, function (e) {
      return "ResNet50" === (t = function (t) {
        if (null == (t = t || Lt).architecture && (t.architecture = "MobileNetV1"), zt.indexOf(t.architecture) < 0) throw new Error("Invalid architecture " + t.architecture + ". Should be one of " + zt);
        if (null == t.outputStride && (t.outputStride = 16), Wt[t.architecture].indexOf(t.outputStride) < 0) throw new Error("Invalid outputStride " + t.outputStride + ". Should be one of " + Wt[t.architecture] + " for architecture " + t.architecture + ".");
        if (null == t.multiplier && (t.multiplier = 1), jt[t.architecture].indexOf(t.multiplier) < 0) throw new Error("Invalid multiplier " + t.multiplier + ". Should be one of " + jt[t.architecture] + " for architecture " + t.architecture + ".");
        if (null == t.quantBytes && (t.quantBytes = 4), Vt.indexOf(t.quantBytes) < 0) throw new Error("Invalid quantBytes " + t.quantBytes + ". Should be one of " + Vt + " for architecture " + t.architecture + ".");
        return t;
      }(t)).architecture ? [2, Yt(t)] : "MobileNetV1" === t.architecture ? [2, Xt(t)] : [2, null];
    });
  });
}
var $t = ["left_face", "right_face", "left_upper_arm_front", "left_upper_arm_back", "right_upper_arm_front", "right_upper_arm_back", "left_lower_arm_front", "left_lower_arm_back", "right_lower_arm_front", "right_lower_arm_back", "left_hand", "right_hand", "torso_front", "torso_back", "left_upper_leg_front", "left_upper_leg_back", "right_upper_leg_front", "right_upper_leg_back", "left_lower_leg_front", "left_lower_leg_back", "right_lower_leg_front", "right_lower_leg_back", "left_feet", "right_feet"],
  Jt = function () {
    function t(t) {
      this.mask = t;
    }
    return t.prototype.toCanvasImageSource = function () {
      return D(this, void 0, void 0, function () {
        return B(this, function (t) {
          return [2, z(this.mask)];
        });
      });
    }, t.prototype.toImageData = function () {
      return D(this, void 0, void 0, function () {
        return B(this, function (t) {
          return [2, this.mask];
        });
      });
    }, t.prototype.toTensor = function () {
      return D(this, void 0, void 0, function () {
        return B(this, function (t) {
          return [2, j(this.mask)];
        });
      });
    }, t.prototype.getUnderlyingType = function () {
      return "imagedata";
    }, t;
  }();
function Zt(t) {
  if (V(t), 255 !== t) throw new Error("Foreground id must be 255 but got " + t);
  return "person";
}
function te(t) {
  if (V(t), t >= $t.length) throw new Error("Invalid body part value " + t);
  return $t[t];
}
var ee = function () {
  function t(t) {
    this.bodyPixModel = t;
  }
  return t.prototype.segmentPeople = function (t, e) {
    return D(this, void 0, void 0, function () {
      var n, r, i, o;
      return B(this, function (a) {
        switch (a.label) {
          case 0:
            return t instanceof ImageBitmap && ((n = document.createElement("canvas")).getContext("2d").drawImage(t, 0, 0), t = n), e.segmentBodyParts ? e.multiSegmentation ? [4, this.bodyPixModel.segmentMultiPersonParts(t, e)] : [3, 2] : [3, 5];
          case 1:
            return i = a.sent(), [3, 4];
          case 2:
            return [4, this.bodyPixModel.segmentPersonParts(t, e)];
          case 3:
            i = [a.sent()], a.label = 4;
          case 4:
            return r = i.map(function (t) {
              var e = t.data,
                n = t.width,
                r = t.height,
                i = new Uint8ClampedArray(n * r * 4).fill(0);
              return e.forEach(function (t, e) {
                -1 === t ? (i[4 * e] = $t.length, i[4 * e + 3] = 0) : (i[4 * e] = t, i[4 * e + 3] = 255);
              }), {
                maskValueToLabel: te,
                mask: new Jt(new ImageData(i, n, r))
              };
            }), [3, 10];
          case 5:
            return e.multiSegmentation ? [4, this.bodyPixModel.segmentMultiPerson(t, e)] : [3, 7];
          case 6:
            return o = a.sent(), [3, 9];
          case 7:
            return [4, this.bodyPixModel.segmentPerson(t, e)];
          case 8:
            o = [a.sent()], a.label = 9;
          case 9:
            r = o.map(function (t) {
              var e = t.data,
                n = t.width,
                r = t.height,
                i = new Uint8ClampedArray(n * r * 4).fill(0);
              return e.forEach(function (t, e) {
                0 === t ? (i[4 * e] = 0, i[4 * e + 3] = 0) : (i[4 * e] = 255, i[4 * e + 3] = 255);
              }), {
                maskValueToLabel: Zt,
                mask: new Jt(new ImageData(i, n, r))
              };
            }), a.label = 10;
          case 10:
            return [2, r];
        }
      });
    });
  }, t.prototype.dispose = function () {
    this.bodyPixModel.dispose();
  }, t.prototype.reset = function () {}, t;
}();
function ne(t) {
  return D(this, void 0, void 0, function () {
    return B(this, function (e) {
      return [2, Gt(t).then(function (t) {
        return new ee(t);
      })];
    });
  });
}
var re = {
  runtime: "mediapipe",
  modelType: "general"
};
var ie = function () {
  function t(t) {
    this.mask = t;
  }
  return t.prototype.toCanvasImageSource = function () {
    return D(this, void 0, void 0, function () {
      return B(this, function (t) {
        return [2, this.mask];
      });
    });
  }, t.prototype.toImageData = function () {
    return D(this, void 0, void 0, function () {
      return B(this, function (t) {
        return [2, W(this.mask)];
      });
    });
  }, t.prototype.toTensor = function () {
    return D(this, void 0, void 0, function () {
      return B(this, function (t) {
        return [2, j(this.mask)];
      });
    });
  }, t.prototype.getUnderlyingType = function () {
    return "canvasimagesource";
  }, t;
}();
function oe(t) {
  return V(t), "person";
}
var ae = function () {
  function t(t) {
    var e,
      n = this;
    this.selfieMode = !1;
    var r;
    if (this.selfieSegmentationSolution = new A({
      locateFile: null !== (e = t.locateFile) && void 0 !== e ? e : function (e, n) {
        return t.solutionPath ? t.solutionPath.replace(/\/+$/, "") + "/" + e : n + "/" + e;
      }
    }), "landscape" === t.modelType) r = 1;else r = 0;
    this.selfieSegmentationSolution.setOptions({
      modelSelection: r,
      selfieMode: this.selfieMode
    }), this.selfieSegmentationSolution.onResults(function (t) {
      n.segmentation = [{
        maskValueToLabel: oe,
        mask: new ie(t.segmentationMask)
      }];
    });
  }
  return t.prototype.segmentPeople = function (t, r) {
    return D(this, void 0, void 0, function () {
      var i, o;
      return B(this, function (a) {
        switch (a.label) {
          case 0:
            return r && r.flipHorizontal && r.flipHorizontal !== this.selfieMode && (this.selfieMode = r.flipHorizontal, this.selfieSegmentationSolution.setOptions({
              selfieMode: this.selfieMode
            })), t instanceof e ? (o = ImageData.bind, [4, n.toPixels(t)]) : [3, 2];
          case 1:
            return i = new (o.apply(ImageData, [void 0, a.sent(), t.shape[1], t.shape[0]]))(), [3, 3];
          case 2:
            i = t, a.label = 3;
          case 3:
            return t = i, [4, this.selfieSegmentationSolution.send({
              image: t
            })];
          case 4:
            return a.sent(), [2, this.segmentation];
        }
      });
    });
  }, t.prototype.dispose = function () {
    this.selfieSegmentationSolution.close();
  }, t.prototype.reset = function () {
    this.selfieSegmentationSolution.reset(), this.segmentation = null, this.selfieMode = !1;
  }, t.prototype.initialize = function () {
    return this.selfieSegmentationSolution.initialize();
  }, t;
}();
function se(t) {
  return D(this, void 0, void 0, function () {
    var e, n;
    return B(this, function (r) {
      switch (r.label) {
        case 0:
          return e = function (t) {
            if (null == t) return C({}, re);
            var e = C({}, t);
            return e.runtime = "mediapipe", null == e.modelType && (e.modelType = re.modelType), e;
          }(t), [4, (n = new ae(e)).initialize()];
        case 1:
          return r.sent(), [2, n];
      }
    });
  });
}
function ue(t, e, n, r) {
  var i = t.width,
    o = t.height,
    a = r ? -1 : 1,
    s = Math.cos(t.rotation),
    u = Math.sin(t.rotation),
    f = t.xCenter,
    l = t.yCenter,
    c = 1 / e,
    d = 1 / n,
    h = new Array(16);
  return h[0] = i * s * a * c, h[1] = -o * u * c, h[2] = 0, h[3] = (-.5 * i * s * a + .5 * o * u + f) * c, h[4] = i * u * a * d, h[5] = o * s * d, h[6] = 0, h[7] = (-.5 * o * s - .5 * i * u * a + l) * d, h[8] = 0, h[9] = 0, h[10] = i * c, h[11] = 0, h[12] = 0, h[13] = 0, h[14] = 0, h[15] = 1, function (t) {
    if (16 !== t.length) throw new Error("Array length must be 16 but got " + t.length);
    return [[t[0], t[1], t[2], t[3]], [t[4], t[5], t[6], t[7]], [t[8], t[9], t[10], t[11]], [t[12], t[13], t[14], t[15]]];
  }(h);
}
function fe(t) {
  return t instanceof e ? {
    height: t.shape[0],
    width: t.shape[1]
  } : {
    height: t.height,
    width: t.width
  };
}
function le(t, e) {
  w.assert(0 !== t.width, function () {
    return e + " width cannot be 0.";
  }), w.assert(0 !== t.height, function () {
    return e + " height cannot be 0.";
  });
}
function ce(t, e) {
  var n = function (t, e, n, r) {
    var i = e - t,
      o = r - n;
    if (0 === i) throw new Error("Original min and max are both " + t + ", range cannot be 0.");
    var a = o / i;
    return {
      scale: a,
      offset: n - t * a
    };
  }(0, 255, e[0], e[1]);
  return r(function () {
    return d(p(t, n.scale), n.offset);
  });
}
function de(t, o, a) {
  var s = o.outputTensorSize,
    f = o.keepAspectRatio,
    l = o.borderMode,
    c = o.outputTensorFloatRange,
    d = fe(t),
    h = function (t, e) {
      return e ? {
        xCenter: e.xCenter * t.width,
        yCenter: e.yCenter * t.height,
        width: e.width * t.width,
        height: e.height * t.height,
        rotation: e.rotation
      } : {
        xCenter: .5 * t.width,
        yCenter: .5 * t.height,
        width: t.width,
        height: t.height,
        rotation: 0
      };
    }(d, a),
    p = function (t, e, n) {
      if (void 0 === n && (n = !1), !n) return {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      };
      var r = e.height,
        i = e.width;
      le(e, "targetSize"), le(t, "roi");
      var o,
        a,
        s = r / i,
        u = t.height / t.width,
        f = 0,
        l = 0;
      return s > u ? (o = t.width, a = t.width * s, l = (1 - u / s) / 2) : (o = t.height / s, a = t.height, f = (1 - s / u) / 2), t.width = o, t.height = a, {
        top: l,
        left: f,
        right: f,
        bottom: l
      };
    }(h, s, f),
    m = ue(h, d.width, d.height, !1),
    g = r(function () {
      var r,
        o = (r = t) instanceof e ? r : n.fromPixels(r),
        a = P(function (t, e, n) {
          return le(n, "inputResolution"), [1 / n.width * t[0][0] * e.width, 1 / n.height * t[0][1] * e.width, t[0][3] * e.width, 1 / n.width * t[1][0] * e.height, 1 / n.height * t[1][1] * e.height, t[1][3] * e.height, 0, 0];
        }(m, d, s), [1, 8]),
        f = "zero" === l ? "constant" : "nearest",
        h = E.transform(u(i(o, "float32")), a, "bilinear", f, 0, [s.height, s.width]);
      return null != c ? ce(h, c) : h;
    });
  return {
    imageTensor: g,
    padding: p,
    transformationMatrix: m
  };
}
function he(t, e, n) {
  return r(function () {
    var r = g(t, [0]),
      i = r.shape[2];
    if (1 === i) {
      var o = r;
      switch (e.activation) {
        case "none":
          break;
        case "sigmoid":
          o = v(o);
          break;
        case "softmax":
          throw new Error("Softmax activation requires two channels.");
        default:
          throw new Error("Activation not supported (" + e.activation + ")");
      }
      var a = n ? E.resizeBilinear(o, [n.height, n.width]) : o;
      return g(a, [2]);
    }
    throw new Error("Unsupported number of tensor channels " + i);
  });
}
var pe = {
    runtime: "tfjs",
    modelType: "general",
    modelUrl: "https://tfhub.dev/mediapipe/tfjs-model/selfie_segmentation/general/1"
  },
  me = {
    flipHorizontal: !1
  },
  ge = {
    outputTensorSize: {
      width: 256,
      height: 256
    },
    keepAspectRatio: !1,
    borderMode: "zero",
    outputTensorFloatRange: [0, 1]
  },
  ve = {
    outputTensorSize: {
      width: 256,
      height: 144
    },
    keepAspectRatio: !1,
    borderMode: "zero",
    outputTensorFloatRange: [0, 1]
  },
  we = {
    activation: "none"
  };
var ye = function () {
  function t(t) {
    this.mask = t;
  }
  return t.prototype.toCanvasImageSource = function () {
    return D(this, void 0, void 0, function () {
      return B(this, function (t) {
        return [2, z(this.mask)];
      });
    });
  }, t.prototype.toImageData = function () {
    return D(this, void 0, void 0, function () {
      return B(this, function (t) {
        return [2, W(this.mask)];
      });
    });
  }, t.prototype.toTensor = function () {
    return D(this, void 0, void 0, function () {
      return B(this, function (t) {
        return [2, this.mask];
      });
    });
  }, t.prototype.getUnderlyingType = function () {
    return "tensor";
  }, t;
}();
function be(t) {
  return V(t), "person";
}
var Se,
  xe = function () {
    function t(t, e) {
      this.modelType = t, this.model = e;
    }
    return t.prototype.segmentPeople = function (t, e) {
      return D(this, void 0, void 0, function () {
        var n,
          i = this;
        return B(this, function (o) {
          return e = function (t) {
            if (null == t) return C({}, me);
            var e = C({}, t);
            return null == e.flipHorizontal && (e.flipHorizontal = me.flipHorizontal), e;
          }(e), null == t ? (this.reset(), [2, []]) : (n = r(function () {
            var e = de(t, "general" === i.modelType ? ge : ve).imageTensor,
              n = I(i.model.predict(e), [0, 0, 0, 1], -1),
              r = fe(t),
              o = he(n, we, r),
              a = u(o, 2),
              s = O(a, [[0, 0], [0, 0], [0, 1]]);
            return _(s, [[0, 0], [0, 0], [0, 2]], "symmetric");
          }), [2, [{
            maskValueToLabel: be,
            mask: new ye(n)
          }]]);
        });
      });
    }, t.prototype.dispose = function () {
      this.model.dispose();
    }, t.prototype.reset = function () {}, t;
  }();
function Me(t) {
  return D(this, void 0, void 0, function () {
    var e, n, r;
    return B(this, function (i) {
      switch (i.label) {
        case 0:
          return e = function (t) {
            if (null == t) return C({}, pe);
            var e = C({}, t);
            if (e.runtime = "tfjs", null == e.modelType && (e.modelType = pe.modelType), "general" !== e.modelType && "landscape" !== e.modelType) throw new Error("Model type must be one of general or landscape, but got " + e.modelType);
            null == e.modelUrl && ("general" === e.modelType ? e.modelUrl = "https://tfhub.dev/mediapipe/tfjs-model/selfie_segmentation/general/1" : e.modelUrl = "https://tfhub.dev/mediapipe/tfjs-model/selfie_segmentation/landscape/1");
            return e;
          }(t), n = "string" == typeof e.modelUrl && e.modelUrl.indexOf("https://tfhub.dev") > -1, [4, H(e.modelUrl, {
            fromTFHub: n
          })];
        case 1:
          return r = i.sent(), [2, new xe(e.modelType, r)];
      }
    });
  });
}
function ke(t, e) {
  return D(this, void 0, void 0, function () {
    var n, r;
    return B(this, function (i) {
      switch (t) {
        case Se.MediaPipeSelfieSegmentation:
          if (n = void 0, null != (r = e)) {
            if ("tfjs" === r.runtime) return [2, Me(r)];
            if ("mediapipe" === r.runtime) return [2, se(r)];
            n = r.runtime;
          }
          throw new Error("Expect modelConfig.runtime to be either 'tfjs' or 'mediapipe', but got " + n);
        case Se.BodyPix:
          return [2, ne(r = e)];
        default:
          throw new Error(t + " is not a supported model name.");
      }
    });
  });
}
!function (t) {
  t.BodyPix = "BodyPix", t.MediaPipeSelfieSegmentation = "MediaPipeSelfieSegmentation";
}(Se || (Se = {}));
var Ee = "blurred",
  Te = "blurred-mask",
  Pe = "mask",
  Ie = "lowres-part-mask",
  Oe = "draw-image",
  _e = {};
function He(t, e, n, r) {
  var i = t.width,
    o = t.height,
    a = e.width,
    s = e.height;
  if (i !== a || o !== s) throw new Error("error: dimensions must match. " + n + " has dimensions " + i + "x" + o + ", " + r + " has dimensions " + a + "x" + s);
}
function Ae(t) {
  if ("undefined" != typeof HTMLCanvasElement && t instanceof HTMLCanvasElement || "undefined" != typeof OffscreenCanvas && t instanceof OffscreenCanvas || "undefined" != typeof HTMLImageElement && t instanceof HTMLImageElement) return function (t) {
    if ("offsetHeight" in t && 0 !== t.offsetHeight && "offsetWidth" in t && 0 !== t.offsetWidth) return [t.offsetHeight, t.offsetWidth];
    if (null != t.height && null != t.width) return [t.height, t.width];
    throw new Error("HTMLImageElement must have height and width attributes set.");
  }(t);
  if ("undefined" != typeof ImageData && t instanceof ImageData) return [t.height, t.width];
  if ("undefined" != typeof HTMLVideoElement && t instanceof HTMLVideoElement) return function (t) {
    return t.hasAttribute("height") && t.hasAttribute("width") ? [t.height, t.width] : [t.videoHeight, t.videoWidth];
  }(t);
  if (t instanceof e) return [t.shape[0], t.shape[1]];
  throw new Error("error: Unknown input type: " + t + ".");
}
function Re(t) {
  return _e[t] || (_e[t] = function () {
    if ("undefined" != typeof document) return document.createElement("canvas");
    if ("undefined" != typeof OffscreenCanvas) return new OffscreenCanvas(0, 0);
    throw new Error("Cannot create a canvas in this context");
  }()), _e[t];
}
function Fe(t, e) {
  var n = Re(e);
  return function (t, e) {
    e.width = t.width, e.height = t.height, e.getContext("2d").putImageData(t, 0, 0);
  }(t, n), n;
}
function Ce(t, r, i, o, a, s) {
  return D(this, void 0, void 0, function () {
    var u, f, l, c;
    return B(this, function (d) {
      switch (d.label) {
        case 0:
          return r instanceof e ? [4, n.toPixels(r)] : [3, 2];
        case 1:
          u = d.sent(), f = Ae(r), l = f[0], c = f[1], r = new ImageData(u, c, l), d.label = 2;
        case 2:
          return r instanceof ImageData && (r = Fe(r, Oe)), null == a || null == s ? t.drawImage(r, i, o) : t.drawImage(r, i, o, a, s), [2];
      }
    });
  });
}
function De(t, e) {
  return D(this, void 0, void 0, function () {
    var n, r, i;
    return B(this, function (o) {
      switch (o.label) {
        case 0:
          return n = Ae(t), r = n[0], i = n[1], e.width = i, e.height = r, [4, Ce(e.getContext("2d"), t, 0, 0, i, r)];
        case 1:
          return o.sent(), [2];
      }
    });
  });
}
function Be(t) {
  var e = t.getContext("2d");
  e.scale(-1, 1), e.translate(-t.width, 0);
}
function Le(t, e, n) {
  return D(this, void 0, void 0, function () {
    return B(this, function (r) {
      switch (r.label) {
        case 0:
          return t.globalCompositeOperation = n, [4, Ce(t, e, 0, 0)];
        case 1:
          return r.sent(), [2];
      }
    });
  });
}
function ze(t, e, n) {
  return D(this, void 0, void 0, function () {
    var r, i, o, a, s, u, f, l;
    return B(this, function (c) {
      switch (c.label) {
        case 0:
          for (r = t.getContext("2d"), i = 0, o = 5, a = 1 / (2 * Math.PI * o * o), s = n < 3 ? 1 : 2, f = -n; f <= n; f += s) for (l = -n; l <= n; l += s) u = a * Math.exp(-(l * l + f * f) / (2 * o * o)), i += u;
          f = -n, c.label = 1;
        case 1:
          if (!(f <= n)) return [3, 6];
          l = -n, c.label = 2;
        case 2:
          return l <= n ? (r.globalAlpha = a * Math.exp(-(l * l + f * f) / (2 * o * o)) / i * n, [4, Ce(r, e, l, f)]) : [3, 5];
        case 3:
          c.sent(), c.label = 4;
        case 4:
          return l += s, [3, 2];
        case 5:
          return f += s, [3, 1];
        case 6:
          return r.globalAlpha = 1, [2];
      }
    });
  });
}
function We(t, e, n) {
  return D(this, void 0, void 0, function () {
    var r, i, o, a;
    return B(this, function (s) {
      switch (s.label) {
        case 0:
          return r = Ae(t), i = r[0], o = r[1], a = n.getContext("2d"), n.width = o, n.height = i, a.clearRect(0, 0, o, i), a.save(), /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ? [4, ze(n, t, e)] : [3, 2];
        case 1:
          return s.sent(), [3, 4];
        case 2:
          return a.filter = "blur(" + e + "px)", [4, Ce(a, t, 0, 0, o, i)];
        case 3:
          s.sent(), s.label = 4;
        case 4:
          return a.restore(), [2];
      }
    });
  });
}
function je(t, e, n) {
  return D(this, void 0, void 0, function () {
    var r;
    return B(this, function (i) {
      switch (i.label) {
        case 0:
          return r = Re(n), 0 !== e ? [3, 2] : [4, De(t, r)];
        case 1:
          return i.sent(), [3, 4];
        case 2:
          return [4, We(t, e, r)];
        case 3:
          i.sent(), i.label = 4;
        case 4:
          return [2, r];
      }
    });
  });
}
function Ve(t, e, n, r, i, o) {
  void 0 === o && (o = {
    r: 0,
    g: 255,
    b: 255,
    a: 255
  });
  for (var a = -i; a <= i; a++) for (var s = -i; s <= i; s++) if (0 !== a && 0 !== s) {
    var u = (e + a) * r + (n + s);
    t[4 * u + 0] = o.r, t[4 * u + 1] = o.g, t[4 * u + 2] = o.b, t[4 * u + 3] = o.a;
  }
}
function Ke(t, e, n, r, i, o, a) {
  void 0 === a && (a = 1);
  for (var s = 0, u = -a; u <= a; u++) for (var f = -a; f <= a; f++) if (0 !== u && 0 !== f) {
    var l = (e + u) * r + (n + f);
    (!i[t[4 * l]] || t[4 * l + 3] < o) && (s += 1);
  }
  return s > 0;
}
function Ue(t, e, n, r, i, o) {
  return void 0 === e && (e = {
    r: 0,
    g: 0,
    b: 0,
    a: 0
  }), void 0 === n && (n = {
    r: 0,
    g: 0,
    b: 0,
    a: 255
  }), void 0 === r && (r = !1), void 0 === i && (i = .5), void 0 === o && (o = Array.from(Array(256).keys())), D(this, void 0, void 0, function () {
    var a, s, u, f, l, c, d, h, p, m, g, v, w, y;
    return B(this, function (b) {
      switch (b.label) {
        case 0:
          return 0 === (a = Array.isArray(t) ? t : [t]).length ? [2, null] : [4, Promise.all(a.map(function (t) {
            return t.mask.toImageData();
          }))];
        case 1:
          for (s = b.sent(), u = s[0], f = u.width, l = u.height, c = new Uint8ClampedArray(f * l * 4), d = Math.round(255 * i), h = new Array(256).fill(!1), o.forEach(function (t) {
            return h[t] = !0;
          }), p = 0; p < l; p++) for (m = 0; m < f; m++) for (c[4 * (g = p * f + m) + 0] = n.r, c[4 * g + 1] = n.g, c[4 * g + 2] = n.b, c[4 * g + 3] = n.a, v = 0, w = s; v < w.length; v++) y = w[v], h[y.data[4 * g]] && y.data[4 * g + 3] >= d && (c[4 * g] = e.r, c[4 * g + 1] = e.g, c[4 * g + 2] = e.b, c[4 * g + 3] = e.a, r && p - 1 >= 0 && p + 1 < l && m - 1 >= 0 && m + 1 < f && Ke(y.data, p, m, f, h, d) && Ve(c, p, m, f, 1));
          return [2, new ImageData(c, f, l)];
      }
    });
  });
}
function qe(t, e, n, r) {
  return void 0 === n && (n = {
    r: 0,
    g: 0,
    b: 0,
    a: 255
  }), void 0 === r && (r = .5), D(this, void 0, void 0, function () {
    var i, o, a, s, u, f, l, c, d, h, p, m, g, v;
    return B(this, function (w) {
      switch (w.label) {
        case 0:
          return 0 === (i = Array.isArray(t) ? t : [t]).length ? [2, null] : [4, Promise.all(i.map(function (t) {
            return t.mask.toImageData();
          }))];
        case 1:
          for (o = w.sent(), a = o[0], s = a.width, u = a.height, f = new Uint8ClampedArray(s * u * 4), l = Math.round(255 * r), c = 0; c < u * s; ++c) for (f[(d = 4 * c) + 0] = n.r, f[d + 1] = n.g, f[d + 2] = n.b, f[d + 3] = n.a, h = 0, p = o; h < p.length; h++) (m = p[h]).data[d + 3] >= l && (g = m.data[d], v = e(g), f[d + 0] = v.r, f[d + 1] = v.g, f[d + 2] = v.b, f[d + 3] = v.a);
          return [2, new ImageData(f, s, u)];
      }
    });
  });
}
function Ne(t, e, n, r, i, o) {
  return void 0 === r && (r = .7), void 0 === i && (i = 0), void 0 === o && (o = !1), D(this, void 0, void 0, function () {
    var a, s, u, f, l;
    return B(this, function (c) {
      switch (c.label) {
        case 0:
          return a = Ae(e), s = a[0], u = a[1], t.width = u, t.height = s, (f = t.getContext("2d")).save(), o && Be(t), [4, Ce(f, e, 0, 0)];
        case 1:
          return c.sent(), f.globalAlpha = r, n ? (He({
            width: u,
            height: s
          }, n, "image", "mask"), [4, je(Fe(n, Pe), i, Te)]) : [3, 3];
        case 2:
          l = c.sent(), f.drawImage(l, 0, 0, u, s), c.label = 3;
        case 3:
          return f.restore(), [2];
      }
    });
  });
}
function Qe(t, e, n, r, i, o, a) {
  return void 0 === r && (r = .7), void 0 === i && (i = 0), void 0 === o && (o = !1), void 0 === a && (a = 10), D(this, void 0, void 0, function () {
    var s, u, f, l, c, d, h;
    return B(this, function (p) {
      switch (p.label) {
        case 0:
          return s = Ae(e), u = s[0], He({
            width: s[1],
            height: u
          }, n, "image", "mask"), [4, je(Fe(n, Pe), i, Te)];
        case 1:
          for (f = p.sent(), t.width = f.width, t.height = f.height, (l = t.getContext("2d")).save(), o && Be(t), c = Re(Ie), d = c.getContext("2d"), c.width = f.width * (1 / a), c.height = f.height * (1 / a), d.drawImage(f, 0, 0, f.width, f.height, 0, 0, c.width, c.height), l.imageSmoothingEnabled = !1, l.drawImage(c, 0, 0, c.width, c.height, 0, 0, t.width, t.height), h = 0; h < c.width; h++) l.beginPath(), l.strokeStyle = "#ffffff", l.moveTo(a * h, 0), l.lineTo(a * h, t.height), l.stroke();
          for (h = 0; h < c.height; h++) l.beginPath(), l.strokeStyle = "#ffffff", l.moveTo(0, a * h), l.lineTo(t.width, a * h), l.stroke();
          return l.globalAlpha = 1 - r, [4, Ce(l, e, 0, 0, f.width, f.height)];
        case 2:
          return p.sent(), l.restore(), [2];
      }
    });
  });
}
function Xe(t, e, n) {
  return D(this, void 0, void 0, function () {
    var r, i;
    return B(this, function (o) {
      switch (o.label) {
        case 0:
          return [4, Ue(t, {
            r: 0,
            g: 0,
            b: 0,
            a: 255
          }, {
            r: 0,
            g: 0,
            b: 0,
            a: 0
          }, !1, e)];
        case 1:
          return r = o.sent(), i = Fe(r, Pe), 0 === n ? [2, i] : [2, je(i, n, Te)];
      }
    });
  });
}
function Ye(t, e, n, r, i, o, a) {
  return void 0 === r && (r = .5), void 0 === i && (i = 3), void 0 === o && (o = 3), void 0 === a && (a = !1), D(this, void 0, void 0, function () {
    var s, u, f, l, c, d;
    return B(this, function (h) {
      switch (h.label) {
        case 0:
          return [4, je(e, i, Ee)];
        case 1:
          return s = h.sent(), t.width = s.width, t.height = s.height, u = t.getContext("2d"), Array.isArray(n) && 0 === n.length ? (u.drawImage(s, 0, 0), [2]) : [4, Xe(n, r, o)];
        case 2:
          return f = h.sent(), u.save(), a && Be(t), l = Ae(e), c = l[0], d = l[1], [4, Ce(u, e, 0, 0, d, c)];
        case 3:
          return h.sent(), [4, Le(u, f, "destination-in")];
        case 4:
          return h.sent(), [4, Le(u, s, "destination-over")];
        case 5:
          return h.sent(), u.restore(), [2];
      }
    });
  });
}
function Ge(t, e, n, r) {
  return D(this, void 0, void 0, function () {
    var i, o;
    return B(this, function (a) {
      switch (a.label) {
        case 0:
          return [4, Ue(t, {
            r: 0,
            g: 0,
            b: 0,
            a: 0
          }, {
            r: 0,
            g: 0,
            b: 0,
            a: 255
          }, !0, n, e)];
        case 1:
          return i = a.sent(), o = Fe(i, Pe), 0 === r ? [2, o] : [2, je(o, r, Te)];
      }
    });
  });
}
function $e(t, e, n, r, i, o, a, s) {
  return void 0 === i && (i = .5), void 0 === o && (o = 3), void 0 === a && (a = 3), void 0 === s && (s = !1), D(this, void 0, void 0, function () {
    var u, f, l, c, d, h;
    return B(this, function (p) {
      switch (p.label) {
        case 0:
          return [4, je(e, o, Ee)];
        case 1:
          return u = p.sent(), t.width = u.width, t.height = u.height, f = t.getContext("2d"), Array.isArray(n) && 0 === n.length ? (f.drawImage(u, 0, 0), [2]) : [4, Ge(n, r, i, a)];
        case 2:
          return l = p.sent(), f.save(), s && Be(t), c = Ae(e), d = c[0], h = c[1], [4, Ce(f, e, 0, 0, h, d)];
        case 3:
          return p.sent(), [4, Le(f, l, "destination-in")];
        case 4:
          return p.sent(), [4, Le(f, u, "destination-over")];
        case 5:
          return p.sent(), f.restore(), [2];
      }
    });
  });
}
export { Se as SupportedModels, $e as blurBodyPart, U as bodyPixMaskValueToRainbowColor, ke as createSegmenter, Ye as drawBokehEffect, Ne as drawMask, Qe as drawPixelatedMask, Ue as toBinaryMask, qe as toColoredMask };